import { Suspense } from "react";
import type { CardGridWithFilters, Article } from "@/generated/sb";
import type { TextHeader } from "@/generated/sb";
import { PrefetchedArticlesData } from "@/types/articles";
import { ISbStoryData } from "storyblok-js-client";
import { storyblokEditable } from "@storyblok/react/rsc";
import { articleResolver } from "@/lib/storyblok/adaptors";
import { deleteAutoSlug } from "@/utils/autoSlug";
import { CardGridWithFiltersClient } from "./CardGridWithFiltersClient";
import { CardGridWithFilters as CardGridWithFiltersUI } from "@repo/ui-parkmobile/sections/CardGridWithFilters";

export type CardGridWithFiltersProps = {
  blok: CardGridWithFilters & {
    lastTextHeader?: TextHeader;
    prefetchedArticlesData?: PrefetchedArticlesData;
  };
};

function resolveArticlesToProps(
  articles: ISbStoryData<Article>[],
  disableDescriptions?: boolean,
) {
  return articles
    .map((article) => {
      const articleData = articleResolver({
        article,
        imageAspectRatio: "1/1",
        disableDescription: disableDescriptions,
      });

      if (!articleData) return null;

      return {
        id: article.uuid,
        image: articleData.image,
        title: {
          content: articleData.title,
          level: "h6" as const,
          fontSize: "p" as const,
          className: disableDescriptions ? "uipm:font-semibold" : undefined,
        },
        text: articleData.description,
        date: article.content.publicationDate || article.created_at,
        link: {
          href: `/${deleteAutoSlug(article.full_slug)}`,
          text: "Read More",
        },
      };
    })
    .filter(
      (article): article is NonNullable<typeof article> => article !== null,
    );
}

export function CardGridWithFilters({
  blok,
  ...props
}: CardGridWithFiltersProps) {
  const prefetchedData = blok.prefetchedArticlesData;

  const preselectedStories = (blok.preselectedArticles || []).filter(
    (article) => typeof article !== "string",
  ) as ISbStoryData<Article>[];

  const preselectedArticlesProps = resolveArticlesToProps(
    preselectedStories,
    blok.disableDescriptions,
  );

  const prefetchedArticles = resolveArticlesToProps(
    (prefetchedData?.articles || []) as ISbStoryData<Article>[],
    blok.disableDescriptions,
  );

  const currentPage = prefetchedData?.page || 1;

  const initialArticles =
    currentPage === 1
      ? [...preselectedArticlesProps, ...prefetchedArticles]
      : prefetchedArticles;

  const totalItems = prefetchedData?.total || initialArticles.length;
  const perPage = prefetchedData?.perPage || 8;
  const totalPages = Math.ceil(totalItems / perPage);

  const staticFallback = (
    <CardGridWithFiltersUI
      backgroundColor={blok.backgroundColor || "none"}
      topPadding={blok.topPadding || "none"}
      bottomPadding={blok.bottomPadding || "none"}
      cardsBackground={(blok.cardsBackground as any) || "none"}
      articles={initialArticles}
      currentPage={currentPage}
      totalPages={totalPages}
      buildPageUrl={(page) => (page === 1 ? "?" : `?page=${page}`)}
      id={blok.anchor}
    />
  );

  return (
    <div {...storyblokEditable(blok)} {...props}>
      <Suspense fallback={staticFallback}>
        <CardGridWithFiltersClient
          blok={blok}
          initialArticles={initialArticles}
          initialPage={currentPage}
          initialTotalPages={totalPages}
        />
      </Suspense>
    </div>
  );
}
